import { memo } from 'react';
import { Box, Tooltip } from '@mui/material';

interface AvgScoreCellProps {
    score: number | null | undefined;
    max?: number;
}

const getAvgScoreColor = (score: number): string => {
    if (score >= 60) return 'green.60';
    if (score >= 40) return 'yellow.60';
    return 'red.60';
};

const getAvgScoreLabel = (score: number): string => {
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Neutral';
    return 'Needs attention';
};

/**
 * Reusable table cell for displaying an average quality score.
 *
 * Thresholds: green ≥60, yellow 40-60, red <40  .
 * Score is expected to be a percentage value between 0-100.
 *
 */
const AvgScoreCell: React.FC<AvgScoreCellProps> = ({ score, max = 100 }) => {
    if (score == null) return 'N/A';

    const value = Math.min(100, Math.max(0, score));
    const formatted = value.toFixed(1);
    const scoreColor = getAvgScoreColor(value);
    const tooltipText = getAvgScoreLabel(value);

    return (
        <Tooltip title={tooltipText} arrow placement="top">
            <Box sx={{ display: 'inline-flex', alignItems: 'baseline', fontSize: '14px', fontVariantNumeric: 'tabular-nums' }}>
                <Box component="span" sx={{ fontWeight: 600, color: scoreColor }}>
                    {formatted}
                </Box>
                <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                    / {max}
                </Box>
            </Box>
        </Tooltip>
    );
};

export default memo(AvgScoreCell);
